"use client";

import { useEffect, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import type { OrbData } from "@/data/orbs";
import type { RingGeometryData } from "@/components/canvas/BootStarfield";

// =============================================================================
// ORBIT FOCUS LAYER
// =============================================================================
// The DOM half of the home orbit. The ring itself is drawn in WebGL
// (BootStarfield), which assistive tech and the Tab key cannot reach, so this
// layer renders one real, named <button> per project node inside <main>.
//
// It is NOT a second navigation system. HomeShell's existing ring key handler
// still owns the arrows (step / hold-to-spin) and the ring state; this layer
// only mirrors that state into the DOM:
//   - roving tabindex: the FRONT node is the single Tab stop (tabIndex 0), the
//     other six are tabIndex -1 but stay in the a11y tree, so a screen-reader
//     user can reach and activate any of the seven directly;
//   - when the ring's front node changes while focus is in this layer, focus
//     follows to the new front node (same "focus the highlighted button" move
//     as OrbitalOverview);
//   - a polite live region announces "n of N: title" for each new front node;
//   - while the front node has keyboard focus, a focus ring is drawn around the
//     front orb, positioned from BootStarfield's own camera projection
//     (geometry.frontOrb). The ring is portalled to <body>: <main> is held at
//     opacity 0 / scale(0.93) by HomeShell's legacy hero-reveal ref, so an
//     outline painted inside it would never show. The button itself also sits
//     over the orb, so screen magnifiers that follow focus land in the right place.
// Enter / Space / click open the node through the same handler a pointer tap
// on the orb uses (HomeShell.handleOrbFocus).

interface OrbitFocusLayerProps {
  orbs: OrbData[];
  activeIndex: number;
  // Same callback a tap on the orb uses: rotate to it (if needed) and open peek.
  onActivate: (index: number) => void;
  geometry: RingGeometryData | null;
  // Idle ring (no peek / read). Only then is the front node's focus ring drawn
  // on the orb; in peek/read the ring moves and focus lives in the dialog.
  isIdle: boolean;
  // Points at the front node's button, so dialogs can return focus to it.
  activeNodeRef: RefObject<HTMLButtonElement | null>;
  lockedSlugs: ReadonlySet<string>;
}

const cleanHeadline = (orb: OrbData) => orb.headline.replace(/\s*\n\s*/g, " ");

// Focus ring sits just outside the orb's own halo (torus at 1.12 × radius).
const RING_PAD = 1.4;

export function OrbitFocusLayer({
  orbs,
  activeIndex,
  onActivate,
  geometry,
  isIdle,
  activeNodeRef,
  lockedSlugs,
}: OrbitFocusLayerProps) {
  const layerRef = useRef<HTMLElement>(null);
  const count = orbs.length;
  const activeOrb = orbs[activeIndex];

  // Announce every change of front node, but not the one the ring boots on.
  // (Adjust-state-on-prop-change during render, not in an effect.)
  const [lastIndex, setLastIndex] = useState(activeIndex);
  const [hasRotated, setHasRotated] = useState(false);
  if (lastIndex !== activeIndex) {
    setLastIndex(activeIndex);
    setHasRotated(true);
  }
  const announcement =
    hasRotated && activeOrb ? `${activeIndex + 1} of ${count}: ${cleanHeadline(activeOrb)}` : "";

  // Focus follows the front node while the viewer is driving the ring from
  // this layer (the roving stop just moved to the new front node's button).
  useEffect(() => {
    const layer = layerRef.current;
    const active = activeNodeRef.current;
    if (layer && active && layer.contains(document.activeElement) && document.activeElement !== active) {
      active.focus({ preventScroll: true });
    }
  }, [activeIndex, activeNodeRef]);

  const front = geometry?.frontOrb;
  const showRing = isIdle && !!front;
  const ringRadius = front ? front.radius * RING_PAD : 0;

  // Keyboard focus on the front node (":focus-visible", so a mouse-driven
  // programmatic focus return doesn't light the ring up).
  const [frontFocusVisible, setFrontFocusVisible] = useState(false);

  return (
    <section ref={layerRef} aria-label="Projects" className="absolute inset-0 pointer-events-none">
      {/* The focused project's title is the main landmark's heading. */}
      {activeOrb && <h2 className="sr-only">{cleanHeadline(activeOrb)}</h2>}
      <p id="orbit-nav-hint" className="sr-only">
        Left and right arrow keys rotate the ring. Enter opens the project.
      </p>
      <ul className="list-none m-0 p-0">
        {orbs.map((orb, i) => {
          const isFront = i === activeIndex;
          const slug = orb.slug ?? orb.id;
          const positioned = isFront && showRing;
          return (
            <li key={orb.id}>
              <button
                ref={isFront ? activeNodeRef : undefined}
                type="button"
                data-orbit-node={slug}
                tabIndex={isFront ? 0 : -1}
                aria-current={isFront ? "true" : undefined}
                aria-describedby={isFront ? "orbit-nav-hint" : undefined}
                onClick={() => onActivate(i)}
                onKeyDown={(e) => {
                  // Activate here and stop the event, so HomeShell's window-level
                  // Enter→peek shortcut doesn't fire a second time.
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    onActivate(i);
                  }
                }}
                onKeyUp={(e) => {
                  if (e.key === " ") e.preventDefault();
                }}
                onFocus={(e) => setFrontFocusVisible(isFront && e.currentTarget.matches(":focus-visible"))}
                onBlur={() => setFrontFocusVisible(false)}
                className={
                  positioned
                    ? "fixed z-[6] rounded-full bg-transparent border-0 p-0 pointer-events-none"
                    : "sr-only"
                }
                style={
                  positioned && front
                    ? {
                        left: front.x - ringRadius,
                        top: front.y - ringRadius,
                        width: ringRadius * 2,
                        height: ringRadius * 2,
                      }
                    : undefined
                }
              >
                <span className="sr-only">
                  {cleanHeadline(orb)}
                  {lockedSlugs.has(slug) ? ", password protected" : ""}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
      {showRing && frontFocusVisible && front &&
        createPortal(
          <div
            aria-hidden="true"
            data-orbit-focus-ring
            className="orbit-focus-ring fixed z-[12] rounded-full pointer-events-none"
            style={{
              left: front.x - ringRadius,
              top: front.y - ringRadius,
              width: ringRadius * 2,
              height: ringRadius * 2,
            }}
          />,
          document.body
        )}
    </section>
  );
}
